import { ChevronRight, Clock, CurrencyDollar, MarkerPin01, Phone } from '@untitled-ui/icons-react';
import classNames from 'classnames';
import moment from 'moment';
import React, { Ref, useEffect, useMemo, useRef, useState } from 'react';

import { Card, Collapsible } from '@myhealth-raccoon/components';

import { CopyTextButton } from './CopyTextButton';
import { GoogleMapStatic } from './GoogleMaps';
import { useAppointmentTypeSelector, useClinicSelector } from 'src/hooks/useAsyncSelector';
import { UmbracoClinicModel } from 'src/services/booking';
import { AutomedSiteModel } from 'src/services/site';
import { isExpressTelehealth } from 'src/utils/appointmentTypes';
import { innerHtml } from 'src/utils/html';
import { clinicName } from 'src/utils/name';

const now = moment();

const allDaysOfWeek = Array(7)
    .fill(0)
    .map((_, i) => {
        const weekday = moment(now).set('isoWeekday', i + 1);
        if (weekday.isBefore(now, 'date')) {
            weekday.add(1, 'week');
        }
        return weekday;
    });

type Props = {
    hideMap?: boolean;
    hideTitle?: boolean;
    onClickCancellationPolicy?(): void;
    onLoad?(clinic: UmbracoClinicModel & AutomedSiteModel): void;
};

export function ClinicInfoCard({ hideMap, hideTitle, onClickCancellationPolicy, onLoad }: Props) {
    const { data: clinic } = useClinicSelector();
    useEffect(() => {
        if (clinic && onLoad) {
            onLoad(clinic);
        }
    }, [onLoad, clinic]);

    const {
        addressHtml,
        latitude: lat,
        longitude: long,
        phoneNumber: tel,
        openingHours,
        specialHours: specialOpeningHours,
    } = clinic || {};

    const addressRef = useRef<HTMLElement>(null);
    const telRef = useRef<HTMLElement>(null);
    const [openingHoursOpen, setOpeningHoursOpen] = useState(false);

    const { data: appointmentType } = useAppointmentTypeSelector();

    const adjustedOpeningHours = useMemo(() => {
        const spOp =
            specialOpeningHours?.map(({ date, title, openingTime, closingTime }) => {
                const day = moment(date);
                const open = moment(openingTime).set({
                    year: day.year(),
                    month: day.month(),
                    date: day.date(),
                });
                const close = moment(closingTime).set({
                    year: day.year(),
                    month: day.month(),
                    date: day.date(),
                });
                return { dayName: title, openingTime: open, closingTime: close };
            }) ?? [];
        return (
            openingHours
                ?.map((oh) => {
                    const openingTime = moment(oh.openingTime);
                    const closingTime = moment(oh.closingTime);

                    const date = moment(now).isoWeekday(oh.dayOfWeek);
                    if (date.isBefore(now, 'date')) {
                        date.add(1, 'week');
                    }

                    const specialDay = spOp.find((d) => d.openingTime.isSame(date, 'date'));
                    if (specialDay) {
                        return specialDay;
                    }

                    openingTime.set({
                        year: date.year(),
                        month: date.month(),
                        date: date.date(),
                    });

                    closingTime.set({
                        year: date.year(),
                        month: date.month(),
                        date: date.date(),
                    });
                    return { openingTime, closingTime, dayName: undefined };
                })
                .sort((a, b) => a.openingTime.valueOf() - b.openingTime.valueOf()) ?? []
        );
    }, [openingHours, specialOpeningHours]);

    const { currentDay, nextDay } = useMemo(() => {
        const currentDay = adjustedOpeningHours[0];

        let nextDay = adjustedOpeningHours[1];
        if (!currentDay || !nextDay) {
            return { currentDay: null, nextDay: null };
        }
        while (
            nextDay.openingTime.isSame(nextDay.closingTime, 'minute') &&
            adjustedOpeningHours.indexOf(nextDay) < adjustedOpeningHours.length - 1
        ) {
            nextDay = adjustedOpeningHours[adjustedOpeningHours.indexOf(nextDay) + 1];
        }
        let openingTime = currentDay.openingTime;
        if (openingTime.isBefore(now)) {
            openingTime = nextDay.openingTime;
        }

        return { currentDay, nextDay };
    }, [adjustedOpeningHours]);

    return (
        <div className="text-base font-medium text-gray-800">
            {!hideMap && lat && long && (
                <div className="h-52 bg-gray-300">
                    <GoogleMapStatic lat={lat} long={long} label={clinicName(clinic)} />
                </div>
            )}
            <Card.Body className="space-y-4">
                {!hideTitle && <h2 className="text-lg font-semibold text-gray-900">{clinicName(clinic)}</h2>}
                {addressHtml && (
                    <div className="flex flex-row gap-2.5">
                        <MarkerPin01 className="h-6 w-6 text-gray-400" />
                        <div
                            className="flex-1"
                            // href={`https://www.google.com/maps/dir/?api=1&destination=${lat}%2C${long}`}
                            ref={addressRef as Ref<HTMLDivElement>}
                            {...innerHtml(addressHtml)}
                        />
                        <div>
                            <CopyTextButton contentRef={addressRef} />
                        </div>
                    </div>
                )}
            </Card.Body>
            {tel && (
                <>
                    <hr />
                    <Card.Body className="flex flex-row gap-2.5">
                        <Phone className="h-6 w-6 text-gray-400" />
                        <div className="block flex-1" ref={telRef as Ref<HTMLDivElement>}>
                            {tel}
                        </div>
                        <div>
                            <CopyTextButton contentRef={telRef} />
                        </div>
                    </Card.Body>
                </>
            )}

            {openingHours && openingHours.length > 0 && currentDay && nextDay && (
                <>
                    <hr />
                    <Card.Body>
                        <Collapsible
                            hideButtonLabel
                            className="p-0"
                            open={openingHoursOpen}
                            setOpen={setOpeningHoursOpen}
                            title={
                                <div className="flex flex-row gap-2.5">
                                    <Clock className="h-6 w-6 text-gray-400" />
                                    <div className="flex flex-row flex-wrap gap-x-1 text-[15px] font-medium text-gray-600">
                                        {now.isBefore(currentDay.closingTime) && now.isAfter(currentDay.openingTime) ? (
                                            // currently open
                                            currentDay.closingTime.diff(now, 'minutes') > 90 ? (
                                                // not closing soon
                                                <>
                                                    <span className="text-success-500 font-semibold">Open Now</span>
                                                    <span>
                                                        •Closes{' '}
                                                        {currentDay.closingTime.format(
                                                            currentDay.closingTime.minutes() === 0 ? 'h A' : 'LT'
                                                        )}
                                                    </span>
                                                </>
                                            ) : (
                                                // closing soon
                                                <>
                                                    <span className="text-warning-500">Closes Soon</span>
                                                    <span>
                                                        •
                                                        {currentDay.closingTime.format(
                                                            currentDay.closingTime.minutes() === 0 ? 'h A' : 'LT'
                                                        )}
                                                    </span>
                                                    <span>
                                                        •Opens{' '}
                                                        {nextDay.openingTime.format(
                                                            nextDay.openingTime.minutes() === 0 ? 'h A ddd' : 'LT ddd'
                                                        )}
                                                    </span>
                                                </>
                                            )
                                        ) : (
                                            // currently closed
                                            <>
                                                <span className="text-error-500">Closed</span>
                                                <span>
                                                    •Opens{' '}
                                                    {(currentDay.openingTime.isAfter(now)
                                                        ? currentDay
                                                        : nextDay
                                                    ).openingTime.format('LT ddd')}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            }
                        >
                            <ol className="space-y-1 text-gray-600">
                                {allDaysOfWeek
                                    .sort((a, b) => a.isoWeekday() - b.isoWeekday())
                                    .map((d) => {
                                        const hours = adjustedOpeningHours.find((a) => a.openingTime.isSame(d, 'date'));
                                        return (
                                            <li className="flex flex-row justify-between" key={d.toString()}>
                                                <p
                                                    className={classNames(
                                                        d.isSame(now, 'date') ? 'font-semibold' : 'font-normal'
                                                    )}
                                                >
                                                    {d.format('dddd')}
                                                    {hours?.dayName && (
                                                        <small className="block text-xs font-normal italic leading-tight">
                                                            ({hours.dayName} - {d.format('DD MMMM')})
                                                        </small>
                                                    )}
                                                </p>
                                                <span
                                                    className={classNames(
                                                        d.isSame(now, 'date') ? 'font-semibold' : 'font-normal'
                                                    )}
                                                >
                                                    {hours && !hours.openingTime.isSame(hours.closingTime, 'minute') ? (
                                                        `${hours.openingTime.format('LT')}
                                                -
                                                ${hours.closingTime.format('LT')}`
                                                    ) : (
                                                        <span className="text-error-500">Closed</span>
                                                    )}
                                                </span>
                                            </li>
                                        );
                                    })}
                            </ol>
                        </Collapsible>
                    </Card.Body>
                </>
            )}
            {onClickCancellationPolicy && appointmentType && !isExpressTelehealth(appointmentType) && (
                <>
                    <hr />
                    <Card.Body as="button" className="flex flex-row gap-2.5" onClick={onClickCancellationPolicy}>
                        <CurrencyDollar className="h-6 w-6 text-gray-400" />
                        <div className="block flex-1">Pricing & Cancellation Info</div>
                        <div>
                            <ChevronRight className="h-6 w-6 text-gray-400" />
                        </div>
                    </Card.Body>
                </>
            )}
        </div>
    );
}
